import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animateScroll as scroll } from 'react-scroll/modules';

import ValutaFilter from '@eika-infrastruktur/ui-valuta-filter';
import { LinkButton } from '@eika/button';
import { MarketCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Modal } from '@eika/modal';

import { fetchOngoingApplicationEuroTaxCleared, fetchOngoingApplications } from '../../actions';
import { getAssetByName } from '../../assets';
import Loader from '../../components/Loader/Loader';
import customHistory from '../../customHistory';
import { AppState } from '../../reducers';
import { formatRegNo } from '../../util/common';
import { getApplicationStatus, getProductIcon, getProductName, getProductUrl } from '../../util/mappings';
import ApplicationStatus from '../../util/mappings/applicationStatus';
import LoanProduct from '../../util/mappings/loanProduct';
import LoanType from '../../util/mappings/loanType';
import routePath from '../../util/mappings/routePath';

const OngoingApplications = () => {
    const dispatch = useDispatch<any>();
    const { ongoingApplications, loader, context, feature } = useSelector((state: AppState) => state);
    const { data } = context.config;
    const { text } = data;
    const {
        landingPageTitle,
        landingPageNoOngoingApplications,
        landingPageButton,
        landingPageLoanUpgradeButton,
        landingPageLoanCommitmentButton,
        landingPageCaseStatus,
        landingPageCaseNumber,
        landingPageRegNo,
        landingPageLoanAmount,
        landingPageStatusTextControl,
        landingPageStatusTextDocumentsSent,
        landingPageStatusTextDocumentsReceived,
        landingPageNewCase,
    } = text;

    useEffect(() => {
        dispatch(fetchOngoingApplications());
        scroll.scrollToTop();
    }, [dispatch]);

    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalDescription, setModalDescription] = useState('');

    const { salgspantActivateLoanFromLc } = feature;

    if (loader.isFetchingOngoingApplications) return <Loader title={text.loadingOngoingApplications} />;

    const { landingPageApplicationInfo } = ongoingApplications;

    if (landingPageApplicationInfo.length === 0) {
        return (
            <div className="ongoing-applications ongoing-applications--none">
                <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                    <>
                        <h1>{landingPageTitle}</h1>
                        <p>{landingPageNoOngoingApplications}</p>
                    </>
                </div>
            </div>
        );
    }

    return (
        <div className="ongoing-applications">
            <div className="sgw-layout">
                <h1 className="sgw-layout-item sgw-layout-item__full-width">{landingPageTitle}</h1>
            </div>

            <div className="sgw-layout ongoing-applications__cards">
                {landingPageApplicationInfo?.map((element: any, index: number) => {
                    const {
                        advisorApplication,
                        applicationId,
                        applicationStatus,
                        lbsBankRegNr,
                        loanAmount,
                        loanProduct,
                        loanType,
                        vehicleRegistrationNumber,
                    } = element;
                    const key = `application_${index}`;
                    const title = getProductName(loanType, loanProduct);
                    const isLbs = lbsBankRegNr !== null;
                    const lbsUrl = isLbs ? `?lbsbnr=${lbsBankRegNr}` : '';
                    const agentApplication = loanProduct === LoanProduct.lendo;
                    const idSeparator = isLbs ? '&' : '?';
                    const applicationIdUrl = `${idSeparator}id=${applicationId}`;

                    const finalUrl = `${window.location.origin}${getProductUrl(
                        advisorApplication,
                        agentApplication,
                        loanType,
                        data,
                    )}${lbsUrl}${applicationIdUrl}`;

                    let helperText = '';
                    if (applicationStatus === ApplicationStatus.control) {
                        helperText = landingPageStatusTextControl;
                    } else if (applicationStatus === ApplicationStatus.documentsSent) {
                        helperText = landingPageStatusTextDocumentsSent;
                    } else if (applicationStatus === ApplicationStatus.documentsComplete) {
                        helperText = landingPageStatusTextDocumentsReceived;
                    } else if (applicationStatus === ApplicationStatus.newCase) {
                        helperText = landingPageNewCase;
                    }

                    let primaryButtonText;

                    if (
                        applicationStatus === ApplicationStatus.approved ||
                        applicationStatus === ApplicationStatus.documentsSent ||
                        (applicationStatus === ApplicationStatus.newCase && loanType === LoanType.loanCommitment) ||
                        (applicationStatus === ApplicationStatus.newCase && loanType === LoanType.vehicleLoan)
                    ) {
                        primaryButtonText =
                            loanType === LoanType.loanCommitment && applicationStatus !== ApplicationStatus.newCase
                                ? landingPageLoanCommitmentButton
                                : landingPageButton;
                    }

                    const secondaryButtonText =
                        salgspantActivateLoanFromLc &&
                        loanType === LoanType.loanCommitment &&
                        applicationStatus === ApplicationStatus.approved
                            ? landingPageLoanUpgradeButton
                            : '';

                    return (
                        <MarketCard
                            key={key}
                            className="sgw-layout-item ongoing-applications-item"
                            title={title}
                            header={
                                <MarketCard.IllustrationHeader
                                    image={{
                                        src: getAssetByName(getProductIcon(loanType, loanProduct)),
                                        alt: title,
                                    }}
                                />
                            }
                            primaryButton={
                                primaryButtonText ? (
                                    <MarketCard.PrimaryButton
                                        onClick={() => {
                                            window.location.replace(finalUrl);
                                        }}
                                    >
                                        {primaryButtonText}
                                    </MarketCard.PrimaryButton>
                                ) : undefined
                            }
                            secondaryButton={
                                secondaryButtonText ? (
                                    <MarketCard.PrimaryButton
                                        onClick={() => {
                                            const appId = applicationId as string;
                                            dispatch(fetchOngoingApplicationEuroTaxCleared(appId));
                                            const path = routePath.loanUpgrade.replace(':appId', appId);
                                            customHistory.push(path);
                                        }}
                                    >
                                        {secondaryButtonText}
                                    </MarketCard.PrimaryButton>
                                ) : undefined
                            }
                        >
                            <div className="ongoing-applications-item__body">
                                {!!applicationStatus && (
                                    <div className="ongoingApplications-list-item">
                                        <div>{landingPageCaseStatus}</div>
                                        <div>
                                            {applicationStatus === ApplicationStatus.control ||
                                            applicationStatus === ApplicationStatus.documentsSent ||
                                            applicationStatus === ApplicationStatus.documentsComplete ||
                                            applicationStatus === ApplicationStatus.newCase ? (
                                                <LinkButton
                                                    icon={IconTypes.INFO_24}
                                                    iconOnRightSide
                                                    onClick={() => {
                                                        setModalTitle(getApplicationStatus(applicationStatus));
                                                        setModalDescription(helperText);
                                                        setShowModal(!showModal);
                                                    }}
                                                >
                                                    {getApplicationStatus(applicationStatus)}
                                                </LinkButton>
                                            ) : (
                                                <>{getApplicationStatus(applicationStatus)}</>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {!!applicationId && (
                                    <div className="ongoingApplications-list-item">
                                        <div>{landingPageCaseNumber}</div>
                                        <div>{applicationId}</div>
                                    </div>
                                )}

                                {!!vehicleRegistrationNumber && (
                                    <div className="ongoingApplications-list-item">
                                        <div>{landingPageRegNo}</div>
                                        <div>{formatRegNo(vehicleRegistrationNumber)}</div>
                                    </div>
                                )}

                                {!!loanAmount && loanType !== LoanType.creditCard && (
                                    <div className="ongoingApplications-list-item">
                                        {loanType !== LoanType.creditCard && <div>{landingPageLoanAmount}</div>}
                                        <div>{ValutaFilter(loanAmount, ' ', 0)} kr</div>
                                    </div>
                                )}
                            </div>
                        </MarketCard>
                    );
                })}
                <Modal
                    id="helper"
                    title={modalTitle}
                    description={modalDescription}
                    onClose={() => setShowModal(false)}
                    show={showModal}
                    appNamespace="no-eika-securedloan-application-web"
                    shouldCloseOnOverlayClick
                />
            </div>
        </div>
    );
};

export default OngoingApplications;
